import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { X, ChefHat, ArrowRight, Check } from 'lucide-react';
import { useGameLoopContext } from '../app/GameProviders';
import { CONSTANTS } from '../data';

interface Props {
  show: boolean;
  onClose: () => void;
}

export function TutorialOverlay({ show, onClose }: Props) {
  const [step, setStep] = useState(0);
  const { discoveredRecipesCount } = useGameLoopContext();

  const steps = (CONSTANTS as any)?.TUTORIAL?.STEPS;
  if (!steps || !Array.isArray(steps) || steps.length === 0) {
    throw new Error('CONSTANTS.TUTORIAL.STEPS is missing or empty');
  }

  const currentStep = steps[step];
  const isLastStep = step === steps.length - 1;

  const handleNext = () => {
    if (isLastStep) {
      onClose();
    } else {
      setStep((s) => s + 1);
    }
  };

  if (!show) return null;

  const target = CONSTANTS.WEEKLY_CYCLE.TUTORIAL_RECIPES_TO_DISCOVER;
  const remaining = Math.max(0, target - discoveredRecipesCount);

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-gradient-to-br from-slate-900/80 via-amber-900/20 to-orange-900/30 z-[2500] flex items-center justify-center p-4"
      >
        {currentStep.highlight && (
          <motion.div
            layoutId="tutorial-highlight"
            className="absolute border-4 border-dashed border-amber-400 rounded-2xl"
            style={currentStep.highlight as React.CSSProperties}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          />
        )}

        <motion.div
          key={step}
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          className="relative bg-gradient-to-br from-slate-800/95 via-amber-900/20 to-orange-900/20 backdrop-blur-md rounded-xl shadow-2xl w-full max-w-md border border-amber-500/30 p-8 text-center"
        >
          <div className="flex justify-center mb-4">
            <ChefHat className="w-10 h-10 text-amber-400" />
          </div>
          <h2 className="text-3xl font-bold text-amber-100 mb-4">{currentStep.title}</h2>
          <p className="text-amber-200 mb-6">{currentStep.text}</p>

          {isLastStep && (
            <div className="mb-6">
              <p className="text-sm text-amber-300 mb-2">Discovery Progress:</p>
              <div className="w-full bg-slate-700 rounded-full h-4">
                <motion.div
                  className="bg-gradient-to-r from-emerald-500 to-green-500 h-4 rounded-full"
                  initial={{ width: 0 }}
                  animate={{ width: `${(discoveredRecipesCount / target) * 100}%` }}
                  transition={{ duration: 0.5, ease: 'easeInOut' }}
                />
              </div>
              <p className="text-sm text-amber-200 mt-2 font-semibold">
                {discoveredRecipesCount} / {target} recipes discovered
              </p>
              <p className="text-xs text-emerald-300 mt-1">
                {remaining > 0 ? `${remaining} more to go!` : 'Goal reached!'}
              </p>
            </div>
          )}

          <div className="flex justify-center">
            <button
              onClick={handleNext}
              className="flex items-center gap-2 bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-600 hover:to-green-600 text-white px-8 py-3 rounded-lg font-semibold transition-all"
            >
              {isLastStep ? "Let's Go!" : 'Next'}
              {isLastStep ? <Check className="w-5 h-5" /> : <ArrowRight className="w-5 h-5" />}
            </button>
          </div>

          {!isLastStep && (
            <button onClick={onClose} className="absolute top-4 right-4 text-amber-400 hover:text-white">
              <X className="w-6 h-6" />
            </button>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
